import type { MenuItemT } from "@/app/constants/menu-items";

export type QueryParamsLikeT = Pick<URLSearchParams, "entries" | "getAll">;

const MATCH_SCORE: Record<
  "none" | "trackedWildcard" | "trackedExact" | "descendantHref",
  number
> = {
  none: 0,
  trackedWildcard: 1,
  trackedExact: 2,
  descendantHref: 3,
};

const DESCENDANT_SPECIFICITY_WEIGHT = 0.001;

export const normalizePath = (path: string) => {
  if (!path) return "/";
  const normalized = path.replace(/\/+$/, "");
  return normalized === "" ? "/" : normalized;
};

const isRoutableHref = (href: string) =>
  href.startsWith("/") || /^https?:\/\//.test(href);

const normalizeHostname = (hostname: string): string =>
  hostname
    .trim()
    .toLowerCase()
    .replace(/^www\./, "");

const LOCALHOST_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

const areEquivalentHosts = (
  expectedHostname: string | null,
  currentHostname?: string | null
): boolean => {
  if (!expectedHostname) {
    return true;
  }

  if (!currentHostname) {
    return false;
  }

  const normalizedExpectedHostname = normalizeHostname(expectedHostname);
  const normalizedCurrentHostname = normalizeHostname(currentHostname);

  if (normalizedExpectedHostname === normalizedCurrentHostname) {
    return true;
  }

  return (
    LOCALHOST_HOSTNAMES.has(normalizedExpectedHostname) &&
    LOCALHOST_HOSTNAMES.has(normalizedCurrentHostname)
  );
};

type ParsedComparableHrefT = {
  path: string;
  params: URLSearchParams;
  hostname: string | null;
};

const parseHref = (href: string): ParsedComparableHrefT => {
  if (href.startsWith("/")) {
    const [path, query = ""] = href.split("?");
    return {
      path: normalizePath(path),
      params: new URLSearchParams(query),
      hostname: null,
    };
  }

  try {
    const parsedUrl = new URL(href);
    return {
      path: normalizePath(parsedUrl.pathname),
      params: new URLSearchParams(parsedUrl.search),
      hostname: parsedUrl.hostname,
    };
  } catch {
    const [path, query = ""] = href.split("?");
    return {
      path: normalizePath(path),
      params: new URLSearchParams(query),
      hostname: null,
    };
  }
};

const hasSubsetQueryParams = (
  currentParams: QueryParamsLikeT,
  expectedParams: URLSearchParams
) => {
  for (const [key, expectedValue] of expectedParams.entries()) {
    const currentValues = currentParams.getAll(key);
    if (!currentValues.includes(expectedValue)) {
      return false;
    }
  }
  return true;
};

const getQueryParamCount = (params: URLSearchParams): number =>
  Array.from(params.entries()).length;

const getHrefMatchSpecificity = (
  href: string,
  currentPathname: string,
  currentParams: QueryParamsLikeT,
  currentHostname?: string | null
): number | null => {
  if (!isRoutableHref(href)) {
    return null;
  }

  const {
    path: expectedPath,
    params: expectedParams,
    hostname: expectedHostname,
  } = parseHref(href);

  if (!areEquivalentHosts(expectedHostname, currentHostname)) {
    return null;
  }

  if (normalizePath(currentPathname) !== expectedPath) {
    return null;
  }

  if (!hasSubsetQueryParams(currentParams, expectedParams)) {
    return null;
  }

  // More explicit expected params are considered a more specific match.
  return getQueryParamCount(expectedParams);
};

const doesHrefMatchCurrentRoute = (
  href: string,
  currentPathname: string,
  currentParams: QueryParamsLikeT,
  currentHostname?: string | null
) => {
  return (
    getHrefMatchSpecificity(
      href,
      currentPathname,
      currentParams,
      currentHostname
    ) !== null
  );
};

const getTrackedPathMatchScore = (
  trackedPath: string,
  currentPathname: string,
  currentParams: QueryParamsLikeT,
  currentHostname?: string | null
): number => {
  if (!isRoutableHref(trackedPath)) {
    return MATCH_SCORE.none;
  }

  if (trackedPath.endsWith("*")) {
    const { path: prefix, hostname: expectedHostname } = parseHref(
      trackedPath.slice(0, -1)
    );
    if (!areEquivalentHosts(expectedHostname, currentHostname)) {
      return MATCH_SCORE.none;
    }

    return normalizePath(currentPathname).startsWith(prefix)
      ? MATCH_SCORE.trackedWildcard
      : MATCH_SCORE.none;
  }

  return doesHrefMatchCurrentRoute(
    trackedPath,
    currentPathname,
    currentParams,
    currentHostname
  )
    ? MATCH_SCORE.trackedExact
    : MATCH_SCORE.none;
};

const getBestMatchingDescendantSpecificity = (
  items: MenuItemT[] | undefined,
  currentPathname: string,
  currentParams: QueryParamsLikeT,
  currentHostname?: string | null
): number | null => {
  if (!items || items.length === 0) {
    return null;
  }

  let bestSpecificity: number | null = null;

  for (const item of items) {
    const currentItemSpecificity = getHrefMatchSpecificity(
      item.href,
      currentPathname,
      currentParams,
      currentHostname
    );

    if (
      currentItemSpecificity !== null &&
      (bestSpecificity === null || currentItemSpecificity > bestSpecificity)
    ) {
      bestSpecificity = currentItemSpecificity;
    }

    const childSpecificity = getBestMatchingDescendantSpecificity(
      item.children,
      currentPathname,
      currentParams,
      currentHostname
    );

    if (
      childSpecificity !== null &&
      (bestSpecificity === null || childSpecificity > bestSpecificity)
    ) {
      bestSpecificity = childSpecificity;
    }
  }

  return bestSpecificity;
};

const getMenuItemMatchScore = (
  item: MenuItemT,
  currentPathname: string,
  currentParams: QueryParamsLikeT,
  currentHostname?: string | null
): number => {
  const bestDescendantSpecificity = getBestMatchingDescendantSpecificity(
    item.children,
    currentPathname,
    currentParams,
    currentHostname
  );
  if (bestDescendantSpecificity !== null) {
    return (
      MATCH_SCORE.descendantHref +
      bestDescendantSpecificity * DESCENDANT_SPECIFICITY_WEIGHT
    );
  }

  if (!item.trackedPaths || item.trackedPaths.length === 0) {
    return MATCH_SCORE.none;
  }

  return item.trackedPaths.reduce<number>((bestScore, trackedPath) => {
    const score = getTrackedPathMatchScore(
      trackedPath,
      currentPathname,
      currentParams,
      currentHostname
    );
    return score > bestScore ? score : bestScore;
  }, MATCH_SCORE.none);
};

export const findMatchedSubmenu = (
  currentPathname: string,
  currentParams: QueryParamsLikeT,
  menuItems: MenuItemT[],
  currentHostname?: string | null
): MenuItemT | null => {
  let bestMatch: MenuItemT | null = null;
  let bestScore: number = MATCH_SCORE.none;

  for (const item of menuItems) {
    const score = getMenuItemMatchScore(
      item,
      currentPathname,
      currentParams,
      currentHostname
    );

    const isDescendantTieBreak =
      score === bestScore &&
      score >= MATCH_SCORE.descendantHref &&
      bestMatch !== null;

    if (score > bestScore || isDescendantTieBreak) {
      bestScore = score;
      bestMatch = item;
    }
  }

  return bestMatch;
};
